import { onScopeDispose, ref, watch } from 'vue';

/**
 * Maintenance status snapshot returned by GET /v1/maintenance/status.
 * Covers both subsystems via the `state` discriminator.
 */
export interface MaintenanceStatus {
  state: 'normal' | 'fence' | 'rule';
  is_active: boolean;
  fence?: {
    mode: 'blocked' | 'read_only';
    reason: string;
    started_at: string;
    expires_at: string;
    retry_after: number;
    message: string;
  } | null;
  rule?: {
    mode: string;
    scope_type: string;
    message: string;
    retry_after: number | null;
    ends_at: string | null;
  } | null;
}

export interface MaintenancePollerOptions {
  /** Endpoint URL to poll. Typically `${apiBaseUrl}/v1/maintenance/status`. */
  url: string;
  /** Polling cadence (ms) when state is 'normal'. Default 60_000 (60s) — proactive detection at low cost. */
  idleIntervalMs?: number;
  /** Polling cadence (ms) when state is 'fence' or 'rule'. Default 5_000 (5s) — fast clear-on-recovery. */
  activeIntervalMs?: number;
  /** Maximum backoff (ms) on poll failure. Default 60_000. */
  maxBackoffMs?: number;
  /**
   * Number of consecutive poll failures before calling onNetworkDown.
   * Default 3. Covers Cloud Run zero-instance scenario where the API is
   * completely unreachable and the client gets a fetch/network error rather
   * than a 503 response.
   */
  failureThreshold?: number;
  /** Called whenever a fresh status payload comes back. App stores hook in here. */
  onStatus?: (status: MaintenanceStatus) => void;
  /** Called when a poll request fails (network down, parse error). Optional. */
  onError?: (err: unknown) => void;
  /**
   * Called once when `failureThreshold` consecutive poll failures are reached
   * (network error, zero Cloud Run instances, DNS failure, etc.). The app should
   * treat this as an implicit maintenance/down state. The next successful
   * onStatus call will update the state with the real maintenance status.
   */
  onNetworkDown?: () => void;
}

/**
 * Proactive maintenance/fence-state poller.
 *
 * Companion to the reactive axios 503 interceptor: that interceptor only
 * catches state changes when an in-flight request gets 503, so an idle
 * tab can sit on a working UI for ~30s+ during a deploy. This poller
 * runs on a timer + Page Visibility API to flip the overlay within ~5s
 * of fence-on / fence-off.
 *
 * Phase 5 of the deploy-fence plan
 * (wiki/future/todo/20260510_150236_deploy_fence_blue_green.md §6, §10).
 *
 * Cadence:
 *   - state=normal → poll every `idleIntervalMs` (default 60s)
 *   - state≠normal → poll every `activeIntervalMs` (default 5s)
 *   - tab hidden → stop polling (resume on next visibilitychange)
 *
 * Implementation notes:
 *   - Uses native `fetch` (not axios) so the poll itself can't trigger
 *     the axios 503 interceptor recursively. The endpoint is exempt
 *     from the fence anyway, but bypassing axios keeps the behavior
 *     unambiguous.
 *   - Exponential backoff on consecutive failures (network down, etc.):
 *     interval doubles up to `maxBackoffMs`.
 *   - Auto-stops on scope disposal (component unmount). Caller can also
 *     stop manually via the returned `stop()` function.
 */
export function useMaintenancePoller(options: MaintenancePollerOptions): {
  start: () => void;
  stop: () => void;
  status: ReturnType<typeof ref<MaintenanceStatus | null>>;
  isPolling: ReturnType<typeof ref<boolean>>;
} {
  const idleIntervalMs = options.idleIntervalMs ?? 60_000;
  const activeIntervalMs = options.activeIntervalMs ?? 5_000;
  const maxBackoffMs = options.maxBackoffMs ?? 60_000;
  const failureThreshold = options.failureThreshold ?? 3;

  const status = ref<MaintenanceStatus | null>(null);
  const isPolling = ref(false);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 0; // 0 = no backoff active; >0 = override of cadence
  let stopped = false;
  let consecutiveFailures = 0;
  let networkDownTriggered = false;

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function nextDelay(): number {
    if (backoffMs > 0) return backoffMs;
    return status.value && status.value.state !== 'normal'
      ? activeIntervalMs
      : idleIntervalMs;
  }

  async function poll(): Promise<void> {
    if (stopped) return;

    try {
      const res = await fetch(options.url, {
        // No credentials needed — endpoint is public + exempt.
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        throw new Error(`poll: HTTP ${res.status}`);
      }
      const body = (await res.json()) as MaintenanceStatus;
      status.value = body;
      options.onStatus?.(body);
      // Reset failure tracking on any successful poll response.
      consecutiveFailures = 0;
      networkDownTriggered = false;
      backoffMs = 0;
    } catch (err) {
      consecutiveFailures++;
      // Fire onNetworkDown once when the failure count reaches the threshold.
      // Subsequent failures don't re-fire — one notification is enough.
      if (consecutiveFailures >= failureThreshold && !networkDownTriggered) {
        networkDownTriggered = true;
        options.onNetworkDown?.();
      }
      backoffMs = backoffMs === 0 ? 1_000 : Math.min(backoffMs * 2, maxBackoffMs);
      options.onError?.(err);
    } finally {
      if (!stopped && !document.hidden) {
        timer = setTimeout(poll, nextDelay());
      }
    }
  }

  function start(): void {
    if (isPolling.value || stopped) return;
    isPolling.value = true;
    // Fire immediately so the first paint after start has a value.
    void poll();
  }

  function stop(): void {
    stopped = true;
    isPolling.value = false;
    clearTimer();
  }

  function onVisibilityChange(): void {
    if (stopped) return;
    if (document.hidden) {
      clearTimer();
    } else if (isPolling.value && timer === null) {
      // Resume immediately on focus to catch fast state changes that
      // happened while the tab was hidden.
      void poll();
    }
  }

  // Poll faster the moment state flips between normal ↔ active so
  // the next poll cadence reflects current truth without waiting a
  // full idleInterval. Respects `document.hidden` for the same reason
  // poll()'s finally does: a hidden tab must not keep polling. Without this
  // guard a state flip schedules a timer even while hidden, which both
  // contradicts "tab hidden → stop polling" and leaves a pending timer that
  // suppresses the immediate resume-on-focus poll in onVisibilityChange.
  watch(
    () => status.value?.state,
    (newState, oldState) => {
      if (!isPolling.value || stopped || document.hidden) return;
      if (newState !== oldState) {
        clearTimer();
        timer = setTimeout(poll, nextDelay());
      }
    },
  );

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  onScopeDispose(() => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    stop();
  });

  return { start, stop, status, isPolling };
}
